'use client';

import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useOrderStore } from '@/store/useOrderStore';
import { useAdminMode } from '@/hooks/useAdminMode';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { haversineDistance } from '@/lib/distance';
import { isLowPriceItem, calcScheduleCharges } from '@/lib/price-calculator';
import type { FixedStop } from '@/types/schedule';

interface Props {
  onComplete: () => void;
  submitLabel?: string;
  submitLoading?: boolean;
}

/** 30분 간격 슬롯 생성 (09:00~21:00) */
function generateSlots(): { time: string; label: string }[] {
  const slots: { time: string; label: string }[] = [];
  for (let h = 9; h < 21; h++) {
    for (const m of [0, 30]) {
      const start = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const endM = m + 30;
      const endH = endM >= 60 ? h + 1 : h;
      const endMin = endM >= 60 ? 0 : endM;
      const end = `${String(endH).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
      slots.push({ time: start, label: `${start}~${end}` });
    }
  }
  return slots;
}

const ALL_SLOTS = generateSlots();

/** KST 현재 시각 */
function getKSTNow(): Date {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utc + 9 * 3600000);
}

/** 시간 문자열 → 분 변환 */
function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

export default function Step4Schedule({ onComplete, submitLabel, submitLoading }: Props) {
  const {
    scheduleDate, designatedPickupSlot, designatedDeliverySlot,
    flexiblePickupMorning, flexiblePickupAfternoon,
    flexibleDeliveryAnytime, showDeliveryTimePanel,
    origin, destination, priceBreakdown,
    setSchedule, quoteId,
    slotConflictNotice, setSlotSelectedAt, setSlotConflictNotice,
  } = useOrderStore();

  const readOnly = !!quoteId;
  const { isAdmin } = useAdminMode();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [today, setToday] = useState<Date | null>(null);
  const [kstNow, setKstNow] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<'pickup' | 'delivery'>('pickup');
  const [fixedStops, setFixedStops] = useState<FixedStop[]>([]);
  const [fixedStopsLoading, setFixedStopsLoading] = useState(false);
  const [closedDates, setClosedDates] = useState<Set<string>>(new Set());
  const [optimalSlots, setOptimalSlots] = useState<Map<string, number>>(new Map());
  const [driverClosedTimes, setDriverClosedTimes] = useState<Set<string>>(new Set());

  // 저가 물품 (기본가 16,000원 이하): 유연시간 기본, 할인 없음, 지정 시 할증
  const itemBasePrice = priceBreakdown?.itemBasePrice ?? 0;
  const lowPrice = isLowPriceItem(itemBasePrice);
  // 유연시간 선택 시 예상 방문 시간 안내 팝업
  const [showFlexInfo, setShowFlexInfo] = useState(false);
  // 저가 물품 수거슬롯 지정 할증 확인 대기 슬롯
  const [pendingSlot, setPendingSlot] = useState<string | null>(null);

  useEffect(() => {
    const now = getKSTNow();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    setToday(todayDate);
    setKstNow(now);
  }, []);

  useEffect(() => {
    if (!today || !origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) return;
    const dates: string[] = [];
    for (let i = 0; i <= 10; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      dates.push(formatDateStr(d));
    }
    const price = priceBreakdown?.totalPrice || 15000;
    fetch(`/api/schedule/date-check?dates=${dates.join(',')}&originLat=${origin.lat}&originLng=${origin.lng}&destLat=${destination.lat}&destLng=${destination.lng}&totalPrice=${price}`)
      .then((r) => r.json())
      .then((data) => {
        const closed = new Set<string>();
        if (data.results) {
          for (const [d, v] of Object.entries(data.results)) {
            if (!(v as { open: boolean }).open) closed.add(d);
          }
        }
        setClosedDates(closed);
      })
      .catch(() => {});
  }, [today, origin?.lat, origin?.lng, destination?.lat, destination?.lng]);

  useEffect(() => {
    if (!scheduleDate) {
      setFixedStops([]);
      setDriverClosedTimes(new Set());
      return;
    }
    setFixedStopsLoading(true);
    Promise.all([
      fetch(`/api/schedule/fixed-stops?date=${scheduleDate}`)
        .then((res) => res.json())
        .then((data) => setFixedStops(data.stops || []))
        .catch(() => setFixedStops([])),
      fetch(`/api/schedule/driver-closed-slots?date=${scheduleDate}`)
        .then((res) => res.json())
        .then((data) => setDriverClosedTimes(new Set<string>(data.closedTimes || [])))
        .catch(() => setDriverClosedTimes(new Set())),
    ]).finally(() => setFixedStopsLoading(false));
  }, [scheduleDate]);

  // 최적 슬롯 할인 fetch
  useEffect(() => {
    if (!scheduleDate || !origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) {
      setOptimalSlots(new Map());
      return;
    }
    const price = priceBreakdown?.totalPrice || 15000;
    fetch(`/api/schedule/optimal-slots?date=${scheduleDate}&originLat=${origin.lat}&originLng=${origin.lng}&destLat=${destination.lat}&destLng=${destination.lng}&totalPrice=${price}`)
      .then((r) => r.json())
      .then((data) => {
        const map = new Map<string, number>();
        if (data.slots) {
          for (const s of data.slots as { time: string; discount: number }[]) {
            map.set(s.time, s.discount);
          }
        }
        setOptimalSlots(map);
      })
      .catch(() => setOptimalSlots(new Map()));
  }, [scheduleDate, origin?.lat, origin?.lng, destination?.lat, destination?.lng]);

  // --- 달력 ---
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days: (Date | null)[] = [];
    for (let i = 0; i < startPadding; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  };

  const formatDateStr = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const isDateEnabled = (date: Date) => {
    if (!today || !kstNow) return false;
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // 일요일: 16시~20시만 가능 (완전 차단 아님)
    if (dateOnly < todayOnly) return false;
    if (dateOnly.getTime() === todayOnly.getTime()) {
      if (kstNow.getHours() >= 18) return false;
    }
    const maxDate = new Date(todayOnly);
    maxDate.setDate(maxDate.getDate() + 10);
    // 견적 프리필 날짜는 +10일 제한 면제
    if (dateOnly > maxDate) {
      if (quoteId && scheduleDate) {
        const prefilledDate = new Date(scheduleDate + 'T00:00:00');
        const prefilledOnly = new Date(prefilledDate.getFullYear(), prefilledDate.getMonth(), prefilledDate.getDate());
        if (dateOnly.getTime() !== prefilledOnly.getTime()) return false;
      } else {
        return false;
      }
    }
    if (closedDates.has(formatDateStr(date))) return false;
    return true;
  };

  const handleDateSelect = (date: Date) => {
    if (readOnly) return;
    setSchedule({
      date: formatDateStr(date),
      pickupSlot: null,
      deliverySlot: null,
      // 저가 물품: 오전+오후 유연이 기본 세팅 (할인 없음, 팝업 미표시)
      flexPickupMorning: lowPrice,
      flexPickupAfternoon: lowPrice,
      pickupDiscount: 0,
      flexDeliveryMorning: false,
      flexDeliveryAfternoon: false,
      flexDeliveryAnytime: false,
      showDeliveryPanel: false,
    });
    setActiveTab('pickup');
  };

  // 저가 물품: 날짜가 이미 선택돼 있고 아무 시간도 선택 안 된 경우 유연 기본 세팅
  // (priceBreakdown 이 늦게 도착하거나 Step4 재진입 시). 사용자가 일부만 끈 상태는 건드리지 않음.
  useEffect(() => {
    if (readOnly) return;
    if (!scheduleDate || !lowPrice) return;
    if (designatedPickupSlot || flexiblePickupMorning || flexiblePickupAfternoon) return;
    setSchedule({ flexPickupMorning: true, flexPickupAfternoon: true, pickupSlot: null, pickupDiscount: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleDate, lowPrice, readOnly]);

  const isSelectedDateToday = (): boolean => {
    if (!today || !scheduleDate) return false;
    return scheduleDate === formatDateStr(today);
  };

  const inferredPickupSlot = useMemo((): string | null => {
    if (designatedPickupSlot) return null;
    if (!origin?.lat || !origin?.lng) return '09:00';

    const isSunday = scheduleDate ? new Date(scheduleDate + 'T00:00:00').getDay() === 0 : false;
    const nowMin = (isSelectedDateToday() && kstNow)
      ? kstNow.getHours() * 60 + kstNow.getMinutes()
      : null;

    for (let h = 9; h < 21; h++) {
      for (const m of [0, 30]) {
        const slotMin = h * 60 + m;
        const slotTimeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        if (driverClosedTimes.has(slotTimeStr)) continue;
        if (isSunday && slotMin < 960) continue;
        if (nowMin !== null && slotMin < nowMin + 90) continue;

        let ok = true;
        for (const stop of fixedStops) {
          const d = Math.round(haversineDistance(origin.lat, origin.lng, stop.lat, stop.lng));
          const t = timeToMinutes(stop.slotTime);
          const rangeMin = Math.min(d * 10 + 10, 100);
          const rangeMax = d * 30 + 1000;
          const diff = slotMin - t;
          const inBefore = diff < 0 && (-diff) >= rangeMin && (-diff) < rangeMax;
          const inAfter = diff > 0 && diff >= rangeMin && diff < rangeMax;
          if (!inBefore && !inAfter) { ok = false; break; }
        }
        if (ok) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designatedPickupSlot, origin?.lat, origin?.lng, fixedStops, driverClosedTimes, kstNow, scheduleDate]);

  /** 선택된 날짜가 일요일인지 */
  const isSelectedDateSunday = (): boolean => {
    if (!scheduleDate) return false;
    const d = new Date(scheduleDate + 'T00:00:00');
    return d.getDay() === 0;
  };

  const isSlotAvailable = (slotTime: string, tab: 'pickup' | 'delivery', skipRouteFit = false): boolean => {
    const slotMin = timeToMinutes(slotTime);

    // 기사가 닫은 슬롯 → 비활성화
    if (driverClosedTimes.has(slotTime)) return false;

    // 일요일: 16:00(960분) 이전 슬롯 비활성화
    if (isSelectedDateSunday() && slotMin < 960) return false;

    if (isSelectedDateToday() && kstNow) {
      const nowMin = kstNow.getHours() * 60 + kstNow.getMinutes();
      if (slotMin < nowMin + 90) return false;
    }

    const bookingLat = tab === 'pickup' ? origin?.lat : destination?.lat;
    const bookingLng = tab === 'pickup' ? origin?.lng : destination?.lng;
    if (!bookingLat || !bookingLng) return true;

    // 근접 할인 슬롯(optimal-slots 가 부여)은 이미 기존 방문지 1km 이내·용량 검증을 통과한
    // "효율적" 슬롯이므로, 경로-적합 휴리스틱(아래 거리·시간 간격 규칙)을 우회한다.
    // (하드 가드: 기사 마감/지난 시간/일요일/coords 는 위에서 이미 처리됨)
    if (skipRouteFit) return true;

    const allStops = [...fixedStops];
    if (tab === 'delivery' && origin?.lat && origin?.lng) {
      const pickupTime = designatedPickupSlot || inferredPickupSlot;
      if (pickupTime) {
        allStops.push({
          orderId: '__self__',
          type: 'pickup',
          slotTime: pickupTime,
          lat: origin.lat,
          lng: origin.lng,
        });
      }
    }

    for (const stop of allStops) {
      const d = Math.round(haversineDistance(bookingLat, bookingLng, stop.lat, stop.lng));
      const t = timeToMinutes(stop.slotTime);
      const rangeMin = Math.min(d * 10 + 10, 100);
      const rangeMax = d * 30 + 1000;
      const diff = slotMin - t;
      const sameOrderPickup = stop.orderId === '__self__' && stop.type === 'pickup';
      const inBefore = !sameOrderPickup && diff < 0 && (-diff) >= rangeMin && (-diff) < rangeMax;
      const inAfter = diff > 0 && diff >= rangeMin && diff < rangeMax;
      if (!inBefore && !inAfter) return false;
    }
    return true;
  };

  const handleSlotSelect = (tab: 'pickup' | 'delivery', slotTime: string) => {
    if (readOnly) return;
    // 저가 물품: 수거슬롯 새로 지정 시 4,000원 할증 확인 팝업 → 확인 후 선택 진행
    if (tab === 'pickup' && lowPrice && designatedPickupSlot !== slotTime) {
      setPendingSlot(slotTime);
      return;
    }
    applySlotSelect(tab, slotTime);
  };

  const applySlotSelect = (tab: 'pickup' | 'delivery', slotTime: string) => {
    // 슬롯 선택 시각 기록(동시주문 충돌 검사 기준) + 이전 충돌 안내 해제
    setSlotSelectedAt(Date.now());
    setSlotConflictNotice(null);
    if (tab === 'pickup') {
      const newPickup = designatedPickupSlot === slotTime ? null : slotTime;
      let resetDelivery = false;
      if (designatedDeliverySlot && newPickup) {
        if (destination?.lat && destination?.lng && origin?.lat && origin?.lng) {
          const d = Math.round(haversineDistance(destination.lat, destination.lng, origin.lat, origin.lng));
          const t = timeToMinutes(newPickup);
          const rangeMin = Math.min(d * 10 + 10, 100);
          const rangeMax = Math.round((d * 30 + 1000) * 1.1);
          const diff = timeToMinutes(designatedDeliverySlot) - t;
          if (!(diff > 0 && diff >= rangeMin && diff < rangeMax)) {
            resetDelivery = true;
          }
        } else if (timeToMinutes(designatedDeliverySlot) <= timeToMinutes(newPickup)) {
          resetDelivery = true;
        }
      }
      // 저가 물품은 최적슬롯 할인 미적용
      const discount = newPickup && !lowPrice ? (optimalSlots.get(newPickup) || 0) : 0;
      setSchedule({
        pickupSlot: newPickup,
        flexPickupMorning: false,
        flexPickupAfternoon: false,
        pickupDiscount: discount,
        ...(resetDelivery ? { deliverySlot: null, flexDeliveryAnytime: showDeliveryTimePanel, deliveryDiscount: 0 } : {}),
      });
    } else {
      const newSlot = designatedDeliverySlot === slotTime ? null : slotTime;
      const discount = newSlot && !lowPrice ? (optimalSlots.get(newSlot) || 0) : 0;
      setSchedule({
        deliverySlot: newSlot,
        flexDeliveryAnytime: !newSlot,
        deliveryDiscount: discount,
      });
    }
  };

  /** 저가 물품 수거슬롯 할증 팝업 확인 → 선택 진행 */
  const confirmPendingSlot = () => {
    if (pendingSlot) applySlotSelect('pickup', pendingSlot);
    setPendingSlot(null);
  };

  const handleFlexToggle = (tab: 'pickup' | 'delivery', period: 'morning' | 'afternoon') => {
    if (readOnly) return;
    if (tab === 'pickup') {
      const current = period === 'morning' ? flexiblePickupMorning : flexiblePickupAfternoon;
      setSchedule({
        pickupSlot: null,
        pickupDiscount: 0,
        ...(period === 'morning'
          ? { flexPickupMorning: !current }
          : { flexPickupAfternoon: !current }),
      });
      // 사용자가 직접 유연을 켤 때마다 예상 방문 시간 안내 팝업 (자동 세팅 시에는 미표시)
      if (!current) setShowFlexInfo(true);
    }
  };

  const handleDeliveryAnytimeToggle = () => {
    if (readOnly) return;
    setSchedule({
      deliverySlot: null,
      flexDeliveryAnytime: true,
    });
  };

  const handleToggleDeliveryPanel = () => {
    if (readOnly) return;
    if (showDeliveryTimePanel) {
      // 패널 닫기 → 배송 필드 초기화
      setSchedule({
        showDeliveryPanel: false,
        deliverySlot: null,
        flexDeliveryAnytime: false,
        flexDeliveryMorning: false,
        flexDeliveryAfternoon: false,
      });
      setActiveTab('pickup');
    } else {
      // 패널 열기 → "수거 후 아무때나 가능" 기본 선택
      setSchedule({
        showDeliveryPanel: true,
        flexDeliveryAnytime: true,
        deliverySlot: null,
      });
      setActiveTab('delivery');
    }
  };

  const pickupSelected = !!designatedPickupSlot || flexiblePickupMorning || flexiblePickupAfternoon;
  const deliverySelected = !showDeliveryTimePanel || !!designatedDeliverySlot || flexibleDeliveryAnytime;
  const isValid = !!scheduleDate && pickupSelected && deliverySelected;

  // 유연시간 버튼 활성화 판단 — 오전(09:00~11:59), 오후(12:00~17:59)에 열린 수거 슬롯 수
  const { morningOpenCount, afternoonOpenCount } = useMemo(() => {
    let morning = 0;
    let afternoon = 0;
    for (const slot of ALL_SLOTS) {
      if (driverClosedTimes.has(slot.time)) continue;
      if (!isSlotAvailable(slot.time, 'pickup')) continue;
      const m = timeToMinutes(slot.time);
      if (m < 12 * 60) morning++;
      else if (m < 18 * 60) afternoon++;
    }
    return { morningOpenCount: morning, afternoonOpenCount: afternoon };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleDate, driverClosedTimes, fixedStops, kstNow, today]);

  const morningFlexEnabled = morningOpenCount >= 3;
  const afternoonFlexEnabled = afternoonOpenCount >= 3;
  const days = getDaysInMonth();
  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

  const flexCount = (flexiblePickupMorning ? 1 : 0) + (flexiblePickupAfternoon ? 1 : 0);
  const hasDesignatedDelivery = !!designatedDeliverySlot;

  // 일정 관련 할인/할증 (할인/할증 상태 표시용)
  const charges = priceBreakdown
    ? calcScheduleCharges({
        baseTotal: priceBreakdown.totalPrice,
        itemBasePrice,
        flexCount,
        hasDesignatedPickup: !!designatedPickupSlot,
        hasDesignatedDelivery,
      })
    : null;

  const [attempted, setAttempted] = useState(false);
  const dateError = attempted && !scheduleDate;
  const pickupError = attempted && !!scheduleDate && !pickupSelected;
  const deliveryError = attempted && showDeliveryTimePanel && !designatedDeliverySlot && !flexibleDeliveryAnytime;

  return (
    <div className="space-y-4">
      {slotConflictNotice && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3">
          <p className="text-sm text-red-700">{slotConflictNotice}</p>
        </div>
      )}
      {readOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            견적 시 선택하신 일정입니다. 변경이 필요하시면 새 견적을 요청해주세요.
          </p>
        </div>
      )}
      <div>
        <p className="text-sm text-gray-500">
          {readOnly ? '아래 일정으로 예약이 진행됩니다' : <>원하는 배송일자를 선택해주세요<span className="text-red-500 ml-0.5">*</span></>}
        </p>
      </div>

      <div className={cn(readOnly && 'pointer-events-none opacity-90')} aria-disabled={readOnly}>

      {/* 달력 */}
      <div className={cn('bg-white border border-gray-200 rounded-lg p-4', dateError && 'border-red-500 ring-2 ring-red-200')}>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <span className="font-bold text-gray-900">
            {currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월
          </span>
          <button
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day, i) => (
            <div
              key={day}
              className={cn(
                'text-center text-xs font-medium py-1',
                i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : 'text-gray-500'
              )}
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((date, i) => {
            if (!date) return <div key={i} className="p-2" />;
            const dateStr = formatDateStr(date);
            const enabled = isDateEnabled(date);
            const isTodayDate = today ? date.getTime() === today.getTime() : false;
            const isSelected = scheduleDate === dateStr;

            return (
              <button
                key={i}
                onClick={() => enabled && handleDateSelect(date)}
                disabled={!enabled}
                className={cn(
                  'p-2 text-sm rounded-lg transition-all',
                  !enabled && 'text-gray-300 cursor-not-allowed',
                  enabled && !isSelected && 'hover:bg-gray-100 text-gray-900',
                  isTodayDate && !isSelected && enabled && 'bg-blue-50 text-[#1A3A5C] font-semibold',
                  isSelected && 'bg-[#1A3A5C] text-white font-semibold'
                )}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
      {dateError && (
        <p className="text-sm text-red-500">배송일자를 선택해주세요</p>
      )}

      {/* 시간 선택 패널 */}
      {scheduleDate && (
        <div className="space-y-3">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* 안내 */}
            <div className="bg-blue-50 px-4 py-2 text-xs text-blue-700">
              ℹ️ 교통 상황에 따라 최대 30분까지 오차가 발생할 수 있으니, 여유있는 일정으로 선택해주시기 바랍니다.<br/>
              ℹ️ 물품 수거 후 배송까지는 일반적으로 3시간 내에 완료됩니다.
            </div>

            {/* 탭 */}
            <div className={cn('grid border-b border-gray-200', showDeliveryTimePanel ? 'grid-cols-2' : 'grid-cols-1')}>
              <button
                onClick={() => setActiveTab('pickup')}
                className={cn(
                  'py-3 text-sm font-medium transition-all text-center',
                  activeTab === 'pickup'
                    ? 'text-[#1A3A5C] border-b-2 border-[#1A3A5C]'
                    : 'text-gray-400 hover:text-gray-600'
                )}
              >
                <div>출발지에서 물품수거 시간</div>
                <div className="text-xs mt-0.5">
                  {designatedPickupSlot
                    ? <span className="text-[#4DA3FF] font-semibold">{designatedPickupSlot}</span>
                    : (flexiblePickupMorning || flexiblePickupAfternoon)
                      ? <span className="text-green-600 font-semibold">
                          {[flexiblePickupMorning && '오전', flexiblePickupAfternoon && '오후'].filter(Boolean).join('+')} 유연
                        </span>
                      : <span className="text-gray-400">미지정</span>
                  }
                </div>
              </button>
              {showDeliveryTimePanel && (
                <button
                  onClick={() => setActiveTab('delivery')}
                  className={cn(
                    'py-3 text-sm font-medium transition-all text-center',
                    activeTab === 'delivery'
                      ? 'text-[#1A3A5C] border-b-2 border-[#1A3A5C]'
                      : 'text-gray-400 hover:text-gray-600'
                  )}
                >
                  <div>도착지로 물품배송 시간</div>
                  <div className="text-xs mt-0.5">
                    {designatedDeliverySlot
                      ? <span className="text-[#4DA3FF] font-semibold">{designatedDeliverySlot}</span>
                      : flexibleDeliveryAnytime
                        ? <span className="text-green-600 font-semibold">수거 후 아무때나</span>
                        : <span className="text-gray-400">미지정</span>
                    }
                  </div>
                </button>
              )}
            </div>

            {/* 슬롯 그리드 */}
            {fixedStopsLoading ? (
              <div className="p-8 flex items-center justify-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                <span className="text-sm">가용 시간 확인 중...</span>
              </div>
            ) : (
              <div className={cn(
                'p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-lg',
                activeTab === 'pickup' && pickupError && 'border border-red-500 ring-2 ring-red-200',
                activeTab === 'delivery' && deliveryError && 'border border-red-500 ring-2 ring-red-200',
              )}>
                {ALL_SLOTS.map((slot) => {
                  const discount = optimalSlots.get(slot.time) || 0;
                  // 근접/최적 할인이 붙은 슬롯은 경로-적합 휴리스틱을 우회해 선택 가능하게 (저가 물품 제외)
                  const rangeAvailable = isSlotAvailable(slot.time, activeTab, discount > 0 && !lowPrice);
                  // 배송탭: 수거 시간 이전 슬롯은 할인이 있어도 비활성화
                  const pickupTime = designatedPickupSlot || inferredPickupSlot;
                  const blockedByPickup = activeTab === 'delivery' && pickupTime && timeToMinutes(slot.time) <= timeToMinutes(pickupTime);
                  const driverClosed = driverClosedTimes.has(slot.time);
                  const available = driverClosed ? false : blockedByPickup ? false : rangeAvailable;
                  const selected =
                    activeTab === 'pickup'
                      ? designatedPickupSlot === slot.time
                      : designatedDeliverySlot === slot.time;
                  // 저가 물품은 할인 슬롯 없음
                  const hasDiscount = available && !selected && discount > 0 && !lowPrice;

                  return (
                    <button
                      key={slot.time}
                      onClick={() => available && handleSlotSelect(activeTab, slot.time)}
                      disabled={!available}
                      className={cn(
                        'py-2 px-1 rounded-lg border text-xs transition-all',
                        !available && 'bg-gray-100 text-gray-400 cursor-not-allowed',
                        available && !selected && !hasDiscount && 'border-gray-200 hover:border-gray-300 text-gray-700',
                        available && !selected && hasDiscount && 'border-green-400 bg-green-50 text-green-800 hover:border-green-500',
                        selected && 'bg-[#1A3A5C] text-white border-[#1A3A5C] font-semibold'
                      )}
                    >
                      {slot.label}
                      {hasDiscount && <span className="block text-[10px] text-green-600 font-semibold">{discount}%↓</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {activeTab === 'pickup' && pickupError && (
              <p className="px-3 pb-1 text-sm text-red-500">수거 시간 또는 유연시간을 선택해주세요</p>
            )}
            {activeTab === 'delivery' && deliveryError && (
              <p className="px-3 pb-1 text-sm text-red-500">배송 시간을 선택해주세요</p>
            )}

            {/* 하단 버튼 영역 */}
            <div className="px-3 pb-3">
              {activeTab === 'pickup' ? (
                /* 수거탭: 오전/오후 유연 + 도착시간도 선택 */
                <div className="flex gap-2">
                  <button
                    onClick={() => handleFlexToggle('pickup', 'morning')}
                    disabled={!morningFlexEnabled && !flexiblePickupMorning}
                    className={cn(
                      'flex-1 py-3 rounded-lg border text-sm font-medium transition-all text-center',
                      !morningFlexEnabled && !flexiblePickupMorning
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : flexiblePickupMorning
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    )}
                  >
                    <div>오전 아무때나</div>
                    {!lowPrice && (
                      <div className={cn(
                        'text-xs font-semibold mt-1',
                        !morningFlexEnabled && !flexiblePickupMorning ? 'text-gray-400' : 'text-green-600',
                      )}>5% 할인</div>
                    )}
                  </button>
                  <button
                    onClick={() => handleFlexToggle('pickup', 'afternoon')}
                    disabled={!afternoonFlexEnabled && !flexiblePickupAfternoon}
                    className={cn(
                      'flex-1 py-3 rounded-lg border text-sm font-medium transition-all text-center',
                      !afternoonFlexEnabled && !flexiblePickupAfternoon
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : flexiblePickupAfternoon
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    )}
                  >
                    <div>오후 아무때나</div>
                    {!lowPrice && (
                      <div className={cn(
                        'text-xs font-semibold mt-1',
                        !afternoonFlexEnabled && !flexiblePickupAfternoon ? 'text-gray-400' : 'text-green-600',
                      )}>5% 할인</div>
                    )}
                  </button>
                  <button
                    onClick={handleToggleDeliveryPanel}
                    className={cn(
                      'flex-1 py-3 rounded-lg border text-sm font-medium transition-all text-center',
                      showDeliveryTimePanel
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    )}
                  >
                    <div>도착시간도 선택</div>
                    <div className="text-xs text-orange-600 font-semibold mt-1">7% 할증</div>
                  </button>
                </div>
              ) : (
                /* 배송탭: 수거 후 아무때나 가능 */
                <div className="flex gap-2">
                  <button
                    onClick={handleDeliveryAnytimeToggle}
                    className={cn(
                      'flex-1 py-3 rounded-lg border text-sm font-medium transition-all text-center',
                      flexibleDeliveryAnytime
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    )}
                  >
                    수거 후 아무때나 가능
                  </button>
                </div>
              )}

              {/* 할인/할증 상태 표시 */}
              {(flexCount > 0 || hasDesignatedDelivery || !!designatedPickupSlot) && (
                <div className="mt-2 text-center text-sm font-medium space-y-1">
                  {lowPrice ? (
                    <>
                      {(charges?.designatedPickupSurcharge ?? 0) > 0 && (
                        <div className="text-orange-700">수거시간 지정 4,000원 할증 적용 중</div>
                      )}
                      {(charges?.singleFlexSurcharge ?? 0) > 0 && (
                        <div className="text-orange-700">유연시간 1개만 선택 · 2,000원 할증 적용 중</div>
                      )}
                    </>
                  ) : (
                    flexCount > 0 && (
                      <div className="text-green-700">수거시간 유연 {flexCount * 5}% 할인 적용 중</div>
                    )
                  )}
                  {hasDesignatedDelivery && (
                    <div className="text-orange-700">도착시간 지정 7% 할증 적용 중</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      </div>

      <Button
        onClick={() => {
          if (!readOnly && !isAdmin && !isValid) {
            setAttempted(true);
            return;
          }
          onComplete();
        }}
        disabled={submitLoading}
        className={cn('w-full', !readOnly && !isAdmin && !isValid && 'bg-gray-300 hover:bg-gray-400 text-gray-700')}
        size="lg"
      >
        {submitLoading ? '요청 중...' : (submitLabel || '다음 단계로')}
      </Button>

      {/* 유연시간 선택 안내 팝업 */}
      <Dialog open={showFlexInfo} onOpenChange={setShowFlexInfo}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">안내</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700 text-center">
            예상 방문 시간은 배송일 전날 저녁 8시 이후에 문자로 안내드립니다.
          </p>
          <Button onClick={() => setShowFlexInfo(false)} className="w-full">
            확인
          </Button>
        </DialogContent>
      </Dialog>

      {/* 저가 물품 수거시간 지정 할증 확인 팝업 */}
      <Dialog open={pendingSlot !== null} onOpenChange={(open) => { if (!open) setPendingSlot(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">안내</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700 text-center">
            기본가 16,000원 이하의 물품은 유연시간이 기본이며, 시간 지정시 4,000원이 할증됩니다.
          </p>
          <Button onClick={confirmPendingSlot} className="w-full">
            확인
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
